/**
 * Predefined occupation list used by the patient registration form.
 *
 * The list is bundled locally so the form works fully offline. The
 * SearchableSelect that consumes it also lets the user type an occupation that
 * is not listed, so this is a convenience list — not a closed enum.
 */

export interface OccupationOption {
  value: string;
  label: string;
  /** Grouping label shown under the option and matched during search. */
  description?: string;
  keywords?: string[];
}

interface OccupationGroup {
  group: string;
  items: string[];
}

const OCCUPATION_GROUPS: OccupationGroup[] = [
  {
    group: 'Medical & Healthcare',
    items: [
      'Doctor',
      'Consultant Physician',
      'Surgeon',
      'Nurse',
      'Midwife',
      'Pharmacist',
      'Dentist',
      'Physiotherapist',
      'Lab Technician',
      'Radiographer',
      'Medical Assistant',
      'Hospital Administrator',
    ],
  },
  {
    group: 'Education',
    items: [
      'Teacher',
      'Professor',
      'Lecturer',
      'Headmaster',
      'School Principal',
      'Tuition Teacher',
      'Education Consultant',
      'Researcher',
    ],
  },
  {
    group: 'Engineering & Technology',
    items: [
      'Software Engineer',
      'Civil Engineer',
      'Electrical Engineer',
      'Mechanical Engineer',
      'Architect',
      'Network Engineer',
      'System Administrator',
      'Web Developer',
      'Graphic Designer',
      'Data Analyst',
      'IT Support Specialist',
      'QA Engineer',
    ],
  },
  {
    group: 'Business & Finance',
    items: [
      'Businessman',
      'Businesswoman',
      'Banker',
      'Bank Manager',
      'Accountant',
      'Auditor',
      'Financial Analyst',
      'Insurance Agent',
      'Entrepreneur',
      'Shop Owner',
      'Managing Director',
      'Marketing Executive',
      'Sales Executive',
      'Human Resources Officer',
      'Consultant',
    ],
  },
  {
    group: 'Government & Public Service',
    items: [
      'Government Officer',
      'Civil Servant',
      'Administrative Officer',
      'Customs Officer',
      'Tax Officer',
      'Diplomat',
      'Postmaster',
      'Municipal Officer',
      'NGO Worker',
      'Social Worker',
    ],
  },
  {
    group: 'Legal',
    items: ['Lawyer', 'Advocate', 'Judge', 'Legal Advisor', 'Notary Public'],
  },
  {
    group: 'Armed Forces & Security',
    items: [
      'Army Officer',
      'Retired Army Officer',
      'Navy Officer',
      'Air Force Officer',
      'Police Officer',
      'Ansar Member',
      'Security Guard',
      'Fire Service Officer',
    ],
  },
  {
    group: 'Agriculture & Fisheries',
    items: ['Farmer', 'Agricultural Officer', 'Poultry Farmer', 'Fisherman', 'Livestock Officer', 'Nursery Owner'],
  },
  {
    group: 'Skilled Trades & Construction',
    items: [
      'Electrician',
      'Plumber',
      'Carpenter',
      'Mason',
      'Welder',
      'Painter',
      'Mechanic',
      'Tailor',
      'Weaver',
      'Goldsmith',
      'Construction Worker',
      'Factory Worker',
    ],
  },
  {
    group: 'Transport',
    items: ['Bus Driver', 'Taxi Driver', 'Rickshaw Puller', 'CNG Driver', 'Truck Driver', 'Delivery Rider', 'Pilot'],
  },
  {
    group: 'Retail, Hospitality & Services',
    items: [
      'Shopkeeper',
      'Salesperson',
      'Street Vendor',
      'Chef',
      'Cook',
      'Waiter',
      'Hotel Manager',
      'Beautician',
      'Barber',
      'Cleaner',
      'Domestic Worker',
      'Housekeeper',
      'Event Manager',
      'Tour Guide',
      'Travel Agent',
      'Real Estate Agent',
    ],
  },
  {
    group: 'Media, Arts & Sports',
    items: [
      'Journalist',
      'News Reporter',
      'Photographer',
      'Videographer',
      'Writer',
      'Editor',
      'Actor',
      'Musician',
      'Artist',
      'Athlete',
      'Coach',
    ],
  },
  {
    group: 'Other',
    items: ['Student', 'Housewife', 'Homemaker', 'Retired', 'Unemployed', 'Freelancer', 'Volunteer', 'Child'],
  },
];

/** Flat dropdown options derived from the grouped list above. */
export const OCCUPATION_OPTIONS: OccupationOption[] = OCCUPATION_GROUPS.flatMap(({ group, items }) =>
  items.map((label) => ({ value: label, label, description: group })),
);

/** Plain occupation names (alphabetical) — handy for quick checks / exports. */
export const OCCUPATIONS: string[] = OCCUPATION_OPTIONS.map((o) => o.value).sort((a, b) => a.localeCompare(b));
